import { useCallback, useEffect, useState } from 'react';
import { T } from '../components/text/TextEdit';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, Settings2, Coins, Layers, Server, Shield, KeyRound, Copy } from 'lucide-react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { SectionCard, Field, Skeleton } from '../components/pipeline/ui';
import WalletDashboard, { GrantCreditsCard } from '../components/wallet/WalletDashboard';
import { PurchaseBundleCard } from '../components/wallet/BundleAdmin';
import { ApplyPlanCard } from '../components/wallet/PlansAdmin';
import { ProvidersEditor } from '../components/wallet/ProvidersEditor';
import { QaConfigCard } from '../components/wallet/QaConfigCard';
import BundleAdmin from '../components/wallet/BundleAdmin';
import PromoAdmin from '../components/wallet/PromoAdmin';
import ReferralAdmin from '../components/wallet/ReferralAdmin';
import PlansAdmin from '../components/wallet/PlansAdmin';
import MoneyDashboard from '../components/wallet/MoneyDashboard';

function valueAt(obj, group, key) {
  return obj?.[group]?.[key] === true;
}

// Read-only user_id with copy-to-clipboard (QA/debug). Shows a short prefix; copies the full id.
export function CopyableUserId({ id }) {
  const { t } = useTranslation();
  const copy = () => {
    navigator.clipboard?.writeText(id);
    toast.success(t('tenantAdmin.idCopied'));
  };
  return (
    <span className="inline-flex items-center gap-1.5" data-testid={`tenant-userid-${id}`}>
      <code className="text-xs text-muted-foreground" title={id}>{String(id).slice(0, 8)}…</code>
      <button
        type="button"
        onClick={copy}
        className="text-muted-foreground hover:text-foreground"
        title={t('tenantAdmin.copyId')}
        aria-label={<T k="tenantAdmin.copyId" />}
        data-testid={`copy-userid-${id}`}
      >
        <Copy className="h-3.5 w-3.5" />
      </button>
    </span>
  );
}

function ToggleRow({ group, keyName, checked, onChange, testId }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-border last:border-0">
      <span className="text-sm text-foreground font-data">{group}.{keyName}</span>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
    </div>
  );
}

function EntitlementToggles({ catalog, value, onToggle, idPrefix }) {
  if (!catalog) return null;
  return (
    <div className="space-y-1" data-testid={`${idPrefix}-toggles`}>
      {Object.entries(catalog).map(([group, keys]) => (
        <div key={group}>
          {keys.map((k) => (
            <ToggleRow
              key={`${group}.${k}`}
              group={group}
              keyName={k}
              checked={valueAt(value, group, k)}
              onChange={(v) => onToggle(group, k, v)}
              testId={`${idPrefix}-toggle-${group}-${k}`}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function CreateTenantDialog({ onCreated }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.tenantAdmin('create_tenant', {
        email: email.trim(),
        password,
        role: role.trim() || undefined,
      });
      toast.success(t('tenantAdmin.created'));
      setOpen(false); setEmail(''); setPassword(''); setRole('');
      onCreated?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="create-tenant-button"><Plus className="h-4 w-4 me-2" /> {t('tenantAdmin.newTenant')}</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-heading"><T k="tenantAdmin.createTitle" /></DialogTitle></DialogHeader>
        <DialogDescription className="sr-only"><T k="tenantAdmin.createTitle" /></DialogDescription>
        <div className="flex flex-col gap-4">
          <Field label={<T k="tenantAdmin.email" />}>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="create-tenant-email" />
          </Field>
          <Field label={<T k="tenantAdmin.password" />}>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="create-tenant-password" />
            <p className="text-xs text-muted-foreground mt-1"><T k="tenantAdmin.passwordHint" /></p>
          </Field>
          <Field label={<T k="tenantAdmin.roleOptional" />}>
            <Input value={role} onChange={(e) => setRole(e.target.value)} placeholder={t('tenantAdmin.rolePlaceholder')} data-testid="create-tenant-role" />
            <p className="text-xs text-muted-foreground mt-1"><T k="tenantAdmin.roleHint" /></p>
          </Field>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy || !email.trim() || password.length < 8} data-testid="create-tenant-confirm">
            {busy ? t('tenantAdmin.creating') : t('tenantAdmin.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InstanceSummary({ ctx, isSuper, onPaymentsChanged }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  if (!ctx) return null;

  // The payment switch is the one flag between a dormant gateway and a live checkout. It used to be
  // display-only here, so turning payments on meant hand-written SQL. Super-admin only.
  const togglePayments = async (enabled) => {
    setBusy(true);
    try {
      await api.tenantAdmin('set_external_payments', { enabled });
      toast.success(enabled ? t('tenantAdmin.paymentsOn') : t('tenantAdmin.paymentsOff'));
      onPaymentsChanged?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const rows = [
    { label: t('tenantAdmin.instProfile'), value: ctx.profile === 'org' ? t('tenantAdmin.profileOrg') : t('tenantAdmin.profilePlatform'), testId: 'inst-profile' },
    { label: t('tenantAdmin.instSku'), value: ctx.instance_sku || t('tenantAdmin.unconfigured'), testId: 'inst-sku' },
    { label: t('tenantAdmin.instLanguage'), value: (ctx.default_language || 'en').toUpperCase(), testId: 'inst-language' },
    { label: t('tenantAdmin.instMetering'), value: ctx.metering_enabled ? t('tenantAdmin.stateOn') : t('tenantAdmin.stateOff'), testId: 'inst-metering' },
    { label: t('tenantAdmin.instSignup'), value: ctx.public_signup_enabled ? t('tenantAdmin.signupOpen') : t('tenantAdmin.signupClosed'), testId: 'inst-signup' },
    { label: t('tenantAdmin.instPayments'), value: ctx.external_payments_enabled ? t('tenantAdmin.stateOn') : t('tenantAdmin.stateOff'), testId: 'inst-payments' },
  ];
  return (
    <SectionCard title={t('tenantAdmin.instanceTitle')} testId="instance-summary-card">
      <p className="text-sm text-muted-foreground -mt-2 flex items-center gap-2"><Server className="h-4 w-4 text-muted-foreground" /> {t('tenantAdmin.instanceHint')}</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-2">
        {rows.map((r) => (
          <div key={r.testId} className="border border-border rounded-md px-4 py-3 bg-muted/50" data-testid={r.testId}>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">{r.label}</div>
            <div className="text-sm font-medium text-foreground font-data mt-0.5">{r.value}</div>
          </div>
        ))}
      </div>

      {isSuper && (
        <div className="mt-4 border-t border-border pt-4 flex items-center gap-3" data-testid="inst-payments-control">
          <Coins className="h-4 w-4 text-warning" />
          <span className="text-sm font-medium text-foreground"><T k="tenantAdmin.paymentsToggle" /></span>
          <Switch
            checked={ctx.external_payments_enabled === true}
            disabled={busy}
            onCheckedChange={togglePayments}
            data-testid="inst-payments-switch"
          />
          <span className="text-xs text-muted-foreground"><T k="tenantAdmin.paymentsToggleHint" /></span>
        </div>
      )}
    </SectionCard>
  );
}

// CA3 calibration. The preset multipliers MODEL enrichment spend that RAGFlow never reports, so
// this puts the three numbers side by side — charged, measured embedding cost, modelled enrichment
// — and says plainly which is which. Compare against the real provider invoice, then tune.
export function IngestEconomicsCard({ isSuper }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [days, setDays] = useState(30);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback((d) => {
    api.tenantAdmin('ingest_economics', { days: d })
      .then(setData)
      .catch(() => setData({ available: false, by_preset: [] }));
  }, []);

  // N-63: the editor used to open on `{}` — four empty boxes with nothing saying what the values
  // currently were, which reads as a form for entering something new rather than changing something
  // that exists ("does not edit anything, it is only a new couple of fields" — Ali). The settings
  // have to be on screen before they can be calibrated, so they are loaded and shown, not just
  // prefilled into inputs.
  // Only the super sees the settings line and the editor, so only the super needs the config. A
  // limited admin is allowed to read it, but fetching what is never drawn is just a wasted call.
  const loadCfg = useCallback(() => {
    if (!isSuper) return;
    api.tenantAdmin('get_wallet_config')
      .then((r) => setCfg(r.config || r || null))
      .catch(() => setCfg(null));
  }, [isSuper]);

  useEffect(() => { load(days); }, [load, days]);
  useEffect(() => { loadCfg(); }, [loadCfg]);

  const save = async () => {
    setBusy(true);
    try {
      await api.tenantAdmin('set_ingest_pricing', form);
      toast.success(t('tenantAdmin.ingestPricingSaved'));
      setForm(null);
      load(days);
      loadCfg();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  // Open the editor on the LIVE values, so every box shows what it is changing from.
  const openForm = () => setForm({
    credits_per_1k: cfg?.ingest_credits_per_1k_tokens ?? '',
    min_credits: cfg?.ingest_min_credits ?? '',
    bytes_per_token: cfg?.ingest_estimate_bytes_per_token ?? '',
    embedding_usd_per_1m: cfg?.embedding_usd_per_1m_tokens ?? '',
    enrichment_usd_per_1m_est: cfg?.enrichment_usd_per_1m_tokens_est ?? '',
    // 063: the multipliers used to live in code and needed a deploy to change.
    tier_standard: cfg?.ingest_tier_standard ?? '',
    tier_deep: cfg?.ingest_tier_deep ?? '',
    tier_graph: cfg?.ingest_tier_graph ?? '',
  });

  const usd = (v) => `$${Number(v || 0).toFixed(4)}`;

  // N-63: `by_preset` only carries presets that were actually RUN, so `deep` (RAPTOR) had no row and
  // the card looked like the product had two presets. Draw the whole catalogue, zeros included, and
  // keep any rollup row whose preset is no longer in the catalogue rather than hiding real history.
  const rollup = data?.by_preset || [];
  const catalog = data?.presets || [];
  const rows = catalog.length === 0 ? rollup : [
    ...catalog.map((p) => {
      const hit = rollup.find((r) => r.preset === p.preset);
      return hit
        ? { ...hit, label: p.label, enrichment: p.enrichment, cost_tier: hit.cost_tier ?? p.cost_tier }
        : { preset: p.preset, label: p.label, enrichment: p.enrichment, cost_tier: p.cost_tier, unused: true };
    }),
    ...rollup.filter((r) => !catalog.some((p) => p.preset === r.preset)),
  ];

  return (
    <SectionCard title={t('tenantAdmin.ingestEconTitle')} testId="ingest-economics-card">
      <p className="text-sm text-muted-foreground -mt-2"><T k="tenantAdmin.ingestEconHint" /></p>

      {data && data.available === false ? (
        <p className="text-sm text-warning mt-2" data-testid="ingest-econ-unavailable">
          {t('tenantAdmin.ingestEconUnavailable')}
        </p>
      ) : (
        <>
          <div className="flex items-center gap-2 mt-3">
            <span className="text-xs text-muted-foreground"><T k="tenantAdmin.ingestEconWindow" /></span>
            {[7, 30, 90].map((d) => (
              <Button key={d} size="sm" variant={days === d ? 'default' : 'outline'}
                onClick={() => setDays(d)} data-testid={`ingest-econ-days-${d}`}>{d}</Button>
            ))}
          </div>

          <div className="overflow-x-auto mt-3">
            <table className="w-full text-sm" data-testid="ingest-econ-table">
              <thead>
                <tr className="text-start text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="text-start py-2 pe-4"><T k="tenantAdmin.ingestPreset" /></th>
                  <th className="text-start py-2 pe-4"><T k="tenantAdmin.ingestTier" /></th>
                  <th className="text-start py-2 pe-4"><T k="tenantAdmin.ingestJobs" /></th>
                  <th className="text-start py-2 pe-4"><T k="tenantAdmin.ingestTokens" /></th>
                  <th className="text-start py-2 pe-4"><T k="tenantAdmin.ingestCharged" /></th>
                  <th className="text-start py-2 pe-4"><T k="tenantAdmin.ingestRevenue" /></th>
                  <th className="text-start py-2 pe-4"><T k="tenantAdmin.ingestEmbedCost" /></th>
                  <th className="text-start py-2"><T k="tenantAdmin.ingestEnrichEst" /></th>
                </tr>
              </thead>
              <tbody className="font-data">
                {rows.map((r) => (
                  <tr key={r.preset} className={`border-b border-border ${r.unused ? 'opacity-60' : ''}`} data-testid={`ingest-econ-${r.preset}`}>
                    <td className="py-2 pe-4 text-foreground">
                      {r.preset}
                      {r.unused && <span className="ms-2 text-xs text-muted-foreground"><T k="tenantAdmin.ingestPresetUnused" /></span>}
                    </td>
                    <td className="py-2 pe-4">{r.cost_tier}×</td>
                    <td className="py-2 pe-4">{Number(r.jobs || 0)} <span className="text-muted-foreground">({Number(r.not_charged || 0)} {t('tenantAdmin.ingestNotCharged')})</span></td>
                    <td className="py-2 pe-4">{Number(r.tokens || 0).toLocaleString()}</td>
                    <td className="py-2 pe-4">{Number(r.credits_charged || 0).toLocaleString()}</td>
                    <td className="py-2 pe-4">{usd(r.revenue_usd)}</td>
                    <td className="py-2 pe-4 text-foreground">{usd(r.embedding_usd)}</td>
                    {/* N-62: a zero here is a fact about the tier, not a missing number. Say so,
                        otherwise it reads as the same kind of bug this card was built to catch. */}
                    <td className="py-2 text-muted-foreground" data-testid={`ingest-econ-enrich-${r.preset}`}>
                      {usd(r.enrichment_usd_est)}
                      {Array.isArray(r.enrichment) && r.enrichment.length === 0 && (
                        <span className="ms-2 text-xs"><T k="tenantAdmin.ingestNoEnrichment" /></span>
                      )}
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={8} className="py-3 text-muted-foreground" data-testid="ingest-econ-empty">
                    {t('tenantAdmin.ingestEconEmpty')}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          <p className="text-xs text-muted-foreground mt-2"><T k="tenantAdmin.ingestEconLegend" /></p>
        </>
      )}

      {isSuper && (
        <div className="mt-4 border-t border-border pt-4">
          {/* The settings that produce every number above. They were invisible before N-63, which
              made the table impossible to calibrate: you could see the result and not the input. */}
          <div className="text-xs text-muted-foreground mb-3" data-testid="ingest-current-settings">
            <span className="font-medium text-foreground"><T k="tenantAdmin.ingestCurrentSettings" /></span>{' '}
            {cfg ? [
              `${t('tenantAdmin.ingestRate')}: ${cfg.ingest_credits_per_1k_tokens}`,
              `${t('tenantAdmin.ingestMin')}: ${cfg.ingest_min_credits}`,
              `${t('tenantAdmin.ingestBytesPerToken')}: ${cfg.ingest_estimate_bytes_per_token}`,
              `${t('tenantAdmin.ingestEmbedPrice')}: ${cfg.embedding_usd_per_1m_tokens}`,
              `${t('tenantAdmin.ingestEnrichPrice')}: ${cfg.enrichment_usd_per_1m_tokens_est}`,
              `${t('tenantAdmin.ingestTiers')}: ${cfg.ingest_tier_standard}× / ${cfg.ingest_tier_deep}× / ${cfg.ingest_tier_graph}×`,
            ].join(' · ') : '—'}
          </div>

          {!form ? (
            <Button variant="outline" size="sm" onClick={openForm} data-testid="ingest-pricing-edit">
              {t('tenantAdmin.ingestPricingEdit')}
            </Button>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <Field label={<T k="tenantAdmin.ingestRate" />} info="tenantAdmin.ingestRateInfo">
                  <Input type="number" step="0.01" value={form.credits_per_1k ?? ''}
                    onChange={(e) => setForm({ ...form, credits_per_1k: e.target.value })}
                    data-testid="ingest-rate-input" />
                </Field>
                <Field label={<T k="tenantAdmin.ingestMin" />} info="tenantAdmin.ingestMinInfo">
                  <Input type="number" value={form.min_credits ?? ''}
                    onChange={(e) => setForm({ ...form, min_credits: e.target.value })}
                    data-testid="ingest-min-input" />
                </Field>
                {/* N-63: the RPC has always taken five numbers and this form sent four, so the
                    reserve estimator could only be changed by SQL — the one thing this card exists
                    to make unnecessary. N-22 corrected it from 4 to 9 that way. */}
                <Field label={<T k="tenantAdmin.ingestBytesPerToken" />} info="tenantAdmin.ingestBytesPerTokenInfo">
                  <Input type="number" value={form.bytes_per_token ?? ''}
                    onChange={(e) => setForm({ ...form, bytes_per_token: e.target.value })}
                    data-testid="ingest-bytes-input" />
                </Field>
                <Field label={<T k="tenantAdmin.ingestEmbedPrice" />} info="tenantAdmin.ingestEmbedPriceInfo">
                  <Input type="number" step="0.01" value={form.embedding_usd_per_1m ?? ''}
                    onChange={(e) => setForm({ ...form, embedding_usd_per_1m: e.target.value })}
                    data-testid="ingest-embed-price-input" />
                </Field>
                <Field label={<T k="tenantAdmin.ingestEnrichPrice" />} info="tenantAdmin.ingestEnrichPriceInfo">
                  <Input type="number" step="0.01" value={form.enrichment_usd_per_1m_est ?? ''}
                    onChange={(e) => setForm({ ...form, enrichment_usd_per_1m_est: e.target.value })}
                    data-testid="ingest-enrich-price-input" />
                </Field>
              </div>

              {/* 063 — the preset multipliers, editable. They were hardcoded in corpus_ingest.ts and
                  needed a deploy to change, which made them the one price on this card you could
                  see the effect of and never adjust.
                  The number is now PURELY a price: what each preset actually runs is decided by its
                  NAME, so repricing graph to 2 does not stop RAPTOR from firing. */}
              <div className="mt-4 border-t border-border pt-4">
                <p className="text-xs font-medium text-foreground mb-2"><T k="tenantAdmin.ingestTiers" /></p>
                <div className="grid sm:grid-cols-3 gap-4">
                  <Field label={<T k="tenantAdmin.ingestTierStandard" />}>
                    <Input type="number" step="0.1" min="0.1" value={form.tier_standard ?? ''}
                      onChange={(e) => setForm({ ...form, tier_standard: e.target.value })}
                      data-testid="ingest-tier-standard-input" />
                  </Field>
                  <Field label={<T k="tenantAdmin.ingestTierDeep" />}>
                    <Input type="number" step="0.1" min="0.1" value={form.tier_deep ?? ''}
                      onChange={(e) => setForm({ ...form, tier_deep: e.target.value })}
                      data-testid="ingest-tier-deep-input" />
                  </Field>
                  <Field label={<T k="tenantAdmin.ingestTierGraph" />}>
                    <Input type="number" step="0.1" min="0.1" value={form.tier_graph ?? ''}
                      onChange={(e) => setForm({ ...form, tier_graph: e.target.value })}
                      data-testid="ingest-tier-graph-input" />
                  </Field>
                </div>
                <p className="text-xs text-muted-foreground mt-2"><T k="tenantAdmin.ingestTiersHint" /></p>
              </div>
              <p className="text-xs text-muted-foreground mt-2"><T k="tenantAdmin.ingestEnrichPriceHint" /></p>
              <div className="mt-3 flex gap-2">
                <Button size="sm" onClick={save} disabled={busy} data-testid="ingest-pricing-save">
                  {t('tenantAdmin.ingestPricingSave')}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setForm(null)} data-testid="ingest-pricing-cancel">
                  {t('tenantAdmin.connCancel2')}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </SectionCard>
  );
}

// The per-user Manage POPUP (Ali, 2026-08-03). Four tabs: the manage controls, the usage
// table, apply-plan, and grants (credits + bundle) — the last two moved OUT of credit
// management, because the admin acting on a user already knows WHICH user: the cards run in
// fixedEmail mode and the retypable email field does not exist here.
function ManageTenant({ detail, catalog, showWallet, isSuper, onClose, onSetRole, onToggle, onWallet, onSetLimited, onSaveCorpora, onGranted }) {
  const { t } = useTranslation();
  const [roleVal, setRoleVal] = useState('');
  const [corporaText, setCorporaText] = useState('');
  const [plans, setPlans] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [peg, setPeg] = useState(0.01);

  useEffect(() => {
    setRoleVal(detail?.tenant?.role || '');
    setCorporaText(Array.isArray(detail?.tenant?.corpora) ? detail.tenant.corpora.join('\n') : '');
  }, [detail]);

  // The plan and grant tabs need the catalogs; load once per open, quietly.
  useEffect(() => {
    if (!detail) return;
    api.tenantAdmin('list_plans').then((r) => setPlans(r.plans || [])).catch(() => {});
    api.tenantAdmin('list_bundles').then((r) => setBundles(r.bundles || [])).catch(() => {});
    api.out('get_billing_config').then((r) => setPeg(Number(r.credit_usd_peg) || 0.01)).catch(() => {});
  }, [detail]);

  if (!detail) return null;
  const email = detail.tenant.email;
  const resolved = detail.resolved_entitlements || {};
  // Per-user overrides expose only paths/plugins. Admin sections resolve at the instance level,
  // never per-user — so they are deliberately not editable here.
  const perUserCatalog = catalog ? { paths: catalog.paths, plugins: catalog.plugins } : null;

  const onApply = async (em, plan_id) => {
    const r = await api.tenantAdmin('apply_plan_by_email', { email: em, plan_id });
    onGranted?.();
    return { email: r.email, wallet: r.wallet };
  };
  const onGrant = async (em, credits, note) => {
    const r = await api.tenantAdmin('grant_credits_by_email', { email: em, credits, note });
    onGranted?.();
    return { email: r.email, wallet: r.wallet };
  };
  const onPurchase = async (em, bundle_id) => {
    const r = await api.tenantAdmin('mark_bundle_purchased', { email: em, bundle_id });
    onGranted?.();
    return { email: r.email, wallet: r.wallet };
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="manage-tenant-dialog">
        <DialogHeader>
          <DialogTitle>{`${t('tenantAdmin.manageTitle')} — ${email}`}</DialogTitle>
        </DialogHeader>
        <Tabs defaultValue="manage">
          <TabsList>
            <TabsTrigger value="manage" data-testid="manage-tab-manage"><T k="tenantAdmin.manage" /></TabsTrigger>
            <TabsTrigger value="usage" data-testid="manage-tab-usage"><T k="tenantAdmin.usageTitle" /></TabsTrigger>
            <TabsTrigger value="plan" data-testid="manage-tab-plan"><T k="plansAdmin.applyTitle" /></TabsTrigger>
            <TabsTrigger value="grants" data-testid="manage-tab-grants"><T k="tenantAdmin.grantsTab" /></TabsTrigger>
          </TabsList>

          <TabsContent value="plan" className="pt-3">
            <ApplyPlanCard plans={plans} peg={peg} onApply={onApply} fixedEmail={email} />
          </TabsContent>

          <TabsContent value="grants" className="pt-3 flex flex-col gap-5">
            <GrantCreditsCard peg={peg} onGrant={onGrant} fixedEmail={email} />
            <PurchaseBundleCard bundles={bundles} peg={peg} onPurchase={onPurchase} fixedEmail={email} />
          </TabsContent>

          <TabsContent value="manage" className="pt-3 flex flex-col gap-5">
      <div className="grid sm:grid-cols-2 gap-6">
        <Field label={<T k="tenantAdmin.roleLabel" />}>
          <div className="flex gap-2">
            <Input value={roleVal} onChange={(e) => setRoleVal(e.target.value)} placeholder={t('tenantAdmin.rolePlaceholder')} data-testid="manage-role-input" />
            <Button variant="outline" disabled={!roleVal.trim()} onClick={() => onSetRole(detail.tenant.id, roleVal.trim())} data-testid="manage-role-save">
              <Shield className="h-4 w-4 me-1.5" /> {t('tenantAdmin.setRole')}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-1"><T k="tenantAdmin.roleHint" /></p>
        </Field>
        {showWallet && (
          <Field label={<T k="tenantAdmin.wallet" />}>
            <div className="flex items-center gap-3">
              <Coins className="h-4 w-4 text-warning" />
              {detail.tenant.is_limited_admin ? (
                // A limited admin is metered by the server regardless of this switch — show it
                // locked ON so the UI cannot promise what the wallet seam will not honour.
                <>
                  <Switch checked disabled data-testid="manage-wallet-switch" />
                  <span className="text-sm text-muted-foreground"><T k="tenantAdmin.alwaysMetered" /></span>
                </>
              ) : (
                <>
                  <Switch
                    checked={resolved?.wallet?.enabled === true}
                    onCheckedChange={(v) => onWallet(detail.tenant.id, v)}
                    data-testid="manage-wallet-switch"
                  />
                  <span className="text-sm text-muted-foreground">{resolved?.wallet?.enabled ? t('tenantAdmin.walletOn') : t('tenantAdmin.walletOff')}</span>
                </>
              )}
            </div>
          </Field>
        )}
        {isSuper && detail.tenant.is_admin !== true && (
          <Field label={<T k="tenantAdmin.limitedAdmin" />}>
            <div className="flex items-center gap-3">
              <Shield className="h-4 w-4 text-muted-foreground" />
              <Switch
                checked={detail.tenant.is_limited_admin === true}
                onCheckedChange={(v) => onSetLimited(detail.tenant.id, v)}
                data-testid="manage-limited-admin-switch"
              />
              <span className="text-sm text-muted-foreground"><T k="tenantAdmin.limitedAdminHint" /></span>
            </div>
          </Field>
        )}
      </div>

      <Field label={<T k="tenantAdmin.entitlements" />}>
        <EntitlementToggles
          catalog={perUserCatalog}
          value={resolved}
          onToggle={(g, k, v) => onToggle(detail.tenant.id, g, k, v)}
          idPrefix="manage"
        />
      </Field>

      <Field label={<T k="tenantAdmin.corpora" />}>
        <Textarea value={corporaText} onChange={(e) => setCorporaText(e.target.value)} className="min-h-[80px] font-data" data-testid="manage-corpora-input" />
        <div className="mt-2">
          <Button variant="outline" size="sm" onClick={() => onSaveCorpora(detail.tenant.id, corporaText)} data-testid="manage-corpora-save">
            {t('tenantAdmin.saveCorpora')}
          </Button>
        </div>
      </Field>
          </TabsContent>

          <TabsContent value="usage" className="pt-3">
        {(!detail.usage || detail.usage.length === 0) ? (
          <p className="text-sm text-muted-foreground" data-testid="manage-no-usage"><T k="tenantAdmin.noUsage" /></p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="manage-usage-table">
              <thead>
                <tr className="text-start text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="text-start py-2 pe-4"><T k="tenantAdmin.tableFunction" /></th>
                  <th className="text-start py-2 pe-4"><T k="tenantAdmin.tableIn" /></th>
                  <th className="text-start py-2 pe-4"><T k="tenantAdmin.tableOut" /></th>
                  <th className="text-start py-2"><T k="tenantAdmin.tableTotal" /></th>
                </tr>
              </thead>
              <tbody className="font-data">
                {detail.usage.map((e) => (
                  <tr key={`${e.function_name}-${e.created_at}`} className="border-b border-border">
                    <td className="py-2 pe-4 text-foreground">{e.function_name}</td>
                    <td className="py-2 pe-4">{e.input_tokens}</td>
                    <td className="py-2 pe-4">{e.output_tokens}</td>
                    <td className="py-2">{e.total_tokens}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function PresetsManager({ presets, catalog, onSaved }) {
  const { t } = useTranslation();
  const [sku, setSku] = useState('');
  const [label, setLabel] = useState('');
  const [ent, setEnt] = useState({});

  const edit = (p) => {
    setSku(p.sku); setLabel(p.label || ''); setEnt(p.entitlements || {});
  };

  const toggle = (group, key, value) => {
    setEnt((prev) => {
      const next = { ...prev, [group]: { ...(prev[group] || {}) } };
      next[group][key] = value;
      return next;
    });
  };

  const save = async () => {
    if (!sku.trim()) { toast.error(t('tenantAdmin.presetSku')); return; }
    try {
      await api.tenantAdmin('upsert_preset', { sku: sku.trim(), label: label.trim() || sku.trim(), entitlements: ent });
      toast.success(t('tenantAdmin.saved'));
      onSaved?.();
    } catch (e) {
      toast.error(e.message);
    }
  };

  // Wallet is a single-key group; merge it into the catalog view for editing. `admin` sections
  // arrive from the catalog already, so packages can be composed as pure data here.
  const editCatalog = { ...(catalog || {}), wallet: ['enabled'] };

  return (
    <SectionCard title={t('tenantAdmin.presetsTitle')} testId="presets-card">
      <p className="text-sm text-muted-foreground -mt-2"><T k="tenantAdmin.presetsHint" /></p>
      <div className="flex flex-wrap gap-2">
        {presets.map((p) => (
          <Button key={p.sku} variant="outline" size="sm" onClick={() => edit(p)} data-testid={`preset-edit-${p.sku}`}>
            <Layers className="h-3.5 w-3.5 me-1.5" /> {p.label || p.sku}
          </Button>
        ))}
        <Button variant="ghost" size="sm" onClick={() => { setSku(''); setLabel(''); setEnt({}); }} data-testid="preset-new">
          <Plus className="h-3.5 w-3.5 me-1.5" /> {t('tenantAdmin.newPreset')}
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 gap-5 mt-4">
        <Field label={<T k="tenantAdmin.presetSku" />}>
          <Input value={sku} onChange={(e) => setSku(e.target.value)} data-testid="preset-sku-input" />
        </Field>
        <Field label={<T k="tenantAdmin.presetLabel" />}>
          <Input value={label} onChange={(e) => setLabel(e.target.value)} data-testid="preset-label-input" />
        </Field>
      </div>

      <Field label={<T k="tenantAdmin.entitlements" />}>
        <EntitlementToggles catalog={editCatalog} value={ent} onToggle={toggle} idPrefix="preset" />
      </Field>
      <div>
        <Button onClick={save} data-testid="preset-save"><Settings2 className="h-4 w-4 me-2" /> {t('tenantAdmin.savePreset')}</Button>
      </div>
    </SectionCard>
  );
}

// AI Models & Providers — the single AI-config home: providers list + keys (ProvidersEditor) +
// the platform default-model selector, with the Model prices table directly below. Lives here
// (not under Packages/Wallet — AI config is infrastructure, not monetization).
function AiModelsProviders({ show }) {
  const [prices, setPrices] = useState([]);
  const loadPrices = useCallback(() => {
    api.tenantAdmin('list_model_prices').then((r) => setPrices(r.model_prices || [])).catch(() => {});
  }, []);
  useEffect(() => { loadPrices(); }, [loadPrices]);

  const upsertLocalPrice = useCallback((row) => {
    if (!row?.model_id) return;
    setPrices((prev) => {
      const i = prev.findIndex((m) => m.model_id === row.model_id);
      if (i === -1) return [...prev, row].sort((a, b) => a.model_id.localeCompare(b.model_id));
      const next = [...prev]; next[i] = row; return next;
    });
  }, []);
  const saveModel = async (payload) => {
    const r = await api.tenantAdmin('set_model_price', payload);
    upsertLocalPrice(r?.model_price);
  };

  return (
    <div className="flex flex-col gap-6" data-testid="ai-models-providers">
      {/* The standalone Model Prices panel is gone (Ali, 2026-08-04): models are defined, priced
          and offered on the ONE providers page below. `show` kept for call-site compatibility. */}
      {show !== 'models'
        ? null
        : (
          <>
            <ProvidersEditor models={prices} onSaveModel={saveModel} onReloadModels={loadPrices} />
            <QaConfigCard models={prices} />
          </>
        )}
    </div>  );
}

const LANES = ['law', 'doc', 'precedent', 'modeled'];
const EMPTY_DIM = { id: null, dimension_key: '', industry_key: '', corpus_lang: '', title: '', description: '', lanes: [], output_type: 'status', maps_to: '', sort_order: 100, active: true };

function DimensionsManager() {
  const { t } = useTranslation();
  const [filterInd, setFilterInd] = useState('');
  const [filterLang, setFilterLang] = useState('');
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY_DIM);

  const load = useCallback(() => {
    api.tenantAdmin('list_dimensions', { industry_key: filterInd || undefined, corpus_lang: filterLang || undefined })
      .then((r) => setRows(r.dimensions || [])).catch((e) => toast.error(e.message));
  }, [filterInd, filterLang]);
  useEffect(() => { load(); }, [load]);

  const toggleLane = (lane) => setForm((f) => ({ ...f, lanes: f.lanes.includes(lane) ? f.lanes.filter((l) => l !== lane) : [...f.lanes, lane] }));

  const save = async () => {
    if (!form.industry_key || !form.corpus_lang || !form.dimension_key || !form.title) {
      toast.error(t('dimAdmin.required')); return;
    }
    try { await api.tenantAdmin('upsert_dimension', { dimension: form }); toast.success(t('tenantAdmin.saved')); setForm(EMPTY_DIM); load(); }
    catch (e) { toast.error(e.message); }
  };
  const del = async (id) => {
    try { await api.tenantAdmin('delete_dimension', { id }); toast.success(t('dimAdmin.deleted')); load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <SectionCard title={t('dimAdmin.title')} testId="dimensions-card">
      <p className="text-sm text-muted-foreground -mt-2"><T k="dimAdmin.intro" /></p>
      <div className="flex flex-wrap gap-3">
        <Input placeholder={t('dimAdmin.filterIndustry')} value={filterInd} onChange={(e) => setFilterInd(e.target.value)} className="max-w-[220px]" data-testid="dim-filter-industry" />
        <Input placeholder={t('dimAdmin.filterLang')} value={filterLang} onChange={(e) => setFilterLang(e.target.value)} className="max-w-[160px]" data-testid="dim-filter-lang" />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="dimensions-table">
          <thead>
            <tr className="text-start text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="text-start py-2 pe-3">#</th><th className="text-start py-2 pe-3"><T k="dimAdmin.colKey" /></th><th className="text-start py-2 pe-3"><T k="dimAdmin.colTitle" /></th>
              <th className="text-start py-2 pe-3"><T k="dimAdmin.colIndLang" /></th><th className="text-start py-2 pe-3"><T k="dimAdmin.colLanes" /></th><th className="text-start py-2 pe-3"><T k="dimAdmin.colActive" /></th><th className="text-start py-2"></th>
            </tr>
          </thead>
          <tbody className="font-data">
            {rows.map((d) => (
              <tr key={d.id} className="border-b border-border" data-testid={`dim-row-${d.dimension_key}`}>
                <td className="py-2 pe-3">{d.sort_order}</td>
                <td className="py-2 pe-3 text-foreground">{d.dimension_key}</td>
                <td className="py-2 pe-3">{d.title}</td>
                <td className="py-2 pe-3">{d.industry_key}/{d.corpus_lang}</td>
                <td className="py-2 pe-3">{(d.lanes || []).join(', ')}</td>
                <td className="py-2 pe-3">{d.active ? '✓' : '—'}</td>
                <td className="py-2 text-end whitespace-nowrap">
                  <Button variant="ghost" size="sm" onClick={() => setForm({ ...EMPTY_DIM, ...d, lanes: d.lanes || [], maps_to: d.maps_to || '' })} data-testid={`dim-edit-${d.dimension_key}`}><T k="dimAdmin.edit" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => del(d.id)} data-testid={`dim-delete-${d.dimension_key}`}><T k="dimAdmin.delete" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="border-t border-border pt-4 mt-2">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-heading font-semibold text-foreground">{form.id ? t('dimAdmin.editDim') : t('dimAdmin.newDim')}</h4>
          <Button variant="ghost" size="sm" onClick={() => setForm(EMPTY_DIM)} data-testid="dim-new"><T k="dimAdmin.new" /></Button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={<T k="dimAdmin.f_dimension_key" />}><Input value={form.dimension_key} onChange={(e) => setForm((f) => ({ ...f, dimension_key: e.target.value }))} data-testid="dim-key" /></Field>
          <Field label={<T k="dimAdmin.f_title" />}><Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="dim-title" /></Field>
          <Field label={<T k="dimAdmin.f_industry_key" />}><Input value={form.industry_key} onChange={(e) => setForm((f) => ({ ...f, industry_key: e.target.value }))} data-testid="dim-industry" /></Field>
          <Field label={<T k="dimAdmin.f_corpus_lang" />}><Input value={form.corpus_lang} onChange={(e) => setForm((f) => ({ ...f, corpus_lang: e.target.value }))} data-testid="dim-lang" /></Field>
          <div className="sm:col-span-2"><Field label={<T k="dimAdmin.description" />}><Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="min-h-[60px]" data-testid="dim-desc" /></Field></div>
          <Field label={<T k="dimAdmin.f_maps_to" />}><Input value={form.maps_to} onChange={(e) => setForm((f) => ({ ...f, maps_to: e.target.value }))} data-testid="dim-mapsto" /></Field>
          <Field label={<T k="dimAdmin.f_sort_order" />}><Input type="number" value={form.sort_order} onChange={(e) => setForm((f) => ({ ...f, sort_order: Number(e.target.value) }))} data-testid="dim-sort" /></Field>
          <div className="sm:col-span-2">
            <Field label={<T k="dimAdmin.lanes" />}>
              <div className="flex flex-wrap gap-2" data-testid="dim-lanes">
                {LANES.map((l) => (
                  <button key={l} type="button" onClick={() => toggleLane(l)} data-testid={`dim-lane-${l}`}
                    className={`px-3 py-1 rounded-full text-sm border ${form.lanes.includes(l) ? 'bg-primary text-primary-foreground border-primary' : 'bg-card text-muted-foreground border-border'}`}>{l}</button>
                ))}
              </div>
            </Field>
          </div>
          <Field label={<T k="dimAdmin.active" />}>
            <Switch checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} data-testid="dim-active" />
          </Field>
        </div>
        <div className="mt-4"><Button onClick={save} data-testid="dim-save"><T k="dimAdmin.saveDim" /></Button></div>
      </div>
    </SectionCard>
  );
}

export default function TenantAdminPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const [allowed, setAllowed] = useState(null);
  // 049 two-tier admin: super sees everything; a limited admin gets this page minus the
  // money-system tabs (config/promo/referral, model prices) — the server enforces the same split.
  const [isSuper, setIsSuper] = useState(false);
  const [ctx, setCtx] = useState(null);
  const [tenants, setTenants] = useState(null);
  const [presets, setPresets] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [detail, setDetail] = useState(null);
  // Users is the one page the mock gives tabs to, and its two halves are already separate
  // components, so it gets them. Credit management's nine tabs are not built - see the
  // commit message.
  const [userTab, setUserTab] = useState('users');
  const [creditTab, setCreditTab] = useState('dashboard');

  const loadTenants = useCallback(() => {
    api.tenantAdmin('list_tenants').then((r) => setTenants(r.tenants || [])).catch((e) => toast.error(e.message));
  }, []);
  const loadCatalog = useCallback(() => {
    api.tenantAdmin('catalog').then((r) => { setPresets(r.presets || []); setCatalog(r.catalog); }).catch(() => {});
  }, []);
  const loadDetail = useCallback((id) => {
    api.tenantAdmin('get_tenant', { tenant_id: id }).then(setDetail).catch((e) => toast.error(e.message));
  }, []);

  useEffect(() => {
    api.getMe().then((r) => {
      if (!r.is_admin && !r.is_limited_admin) { setAllowed(false); navigate('/'); return; }
      setIsSuper(!!r.is_admin);
      setAllowed(true);
      api.tenantAdmin('admin_context').then((c) => {
        setCtx(c);
        if (c.sections?.users) loadTenants();
      }).catch(() => {
        // Fail-open (never silently lock an admin out): assume full platform surface.
        setCtx({ profile: 'platform', instance_sku: null, metering_enabled: false, external_payments_enabled: false, public_signup_enabled: true, default_language: 'en', sections: { users: true, packages: true, dimensions: true, instance: true } });
        loadTenants();
      });
      loadCatalog();
    }).catch(() => { setAllowed(false); navigate('/'); });
  }, [navigate, loadTenants, loadCatalog]);

  const setRole = async (id, role) => {
    try { await api.tenantAdmin('set_role', { tenant_id: id, role }); toast.success(t('tenantAdmin.roleUpdated')); loadDetail(id); loadTenants(); }
    catch (e) { toast.error(e.message); }
  };
  const toggleEnt = async (id, group, key, value) => {
    try { await api.tenantAdmin('set_entitlement', { tenant_id: id, group, key, value }); loadDetail(id); loadTenants(); }
    catch (e) { toast.error(e.message); }
  };
  const setWallet = async (id, enabled) => {
    try { await api.tenantAdmin('set_wallet', { tenant_id: id, enabled }); loadDetail(id); loadTenants(); }
    catch (e) { toast.error(e.message); }
  };
  const setLimitedAdmin = async (id, enabled) => {
    try { await api.tenantAdmin('set_limited_admin', { tenant_id: id, enabled }); toast.success(t('tenantAdmin.saved')); loadDetail(id); loadTenants(); }
    catch (e) { toast.error(e.message); }
  };
  const saveCorpora = async (id, text) => {
    const corpora = text.split('\n').map((s) => s.trim()).filter(Boolean);
    try { await api.tenantAdmin('set_corpora', { tenant_id: id, corpora }); toast.success(t('tenantAdmin.saved')); loadDetail(id); }
    catch (e) { toast.error(e.message); }
  };

  if (allowed === null || ctx === null) {
    return <Skeleton className="h-10 w-48" />;
  }
  if (allowed === false) return null;

  const sections = ctx.sections || {};
  const showWallet = sections.packages === true;

  // Which of the five admin pages is open comes from the ROUTE, the same way Settings does it,
  // so each is linkable and the sidebar can light the right row. One component still renders
  // all five: this is a regrouping of sections that already existed, not a rewrite of them.
  let page = pathname.startsWith('/admin/users') ? 'users'
    : pathname.startsWith('/admin/models') ? 'models'
    : pathname.startsWith('/admin/credits') ? 'credits'
    : pathname.startsWith('/admin/dimensions') ? 'dimensions'
    : 'tenants';
  // Model prices are cost-side (super-only, and their RPCs reject a limited admin anyway) —
  // a typed /admin/models URL falls back to the users page instead of a wall of errors.
  if (!isSuper && page === 'models') page = 'users';

  const TITLE = {
    tenants: 'nav.adminTenants',
    users: 'nav.adminUsers',
    models: 'nav.adminModels',
    credits: 'nav.adminCredit',
    dimensions: 'nav.adminPathb',
  };

  return (
    <>
      <div className="flex items-center gap-3">
        <h1 className="font-heading text-3xl font-bold">{t(TITLE[page])}</h1>
        {page === 'tenants' && (
          <span
            className="inline-flex rounded-full border border-border bg-muted px-2 py-0.5 font-data text-xs text-muted-foreground"
            data-testid="admin-profile-badge"
          >
            {ctx.profile === 'org' ? t('tenantAdmin.profileOrg') : t('tenantAdmin.profilePlatform')}
          </span>
        )}
        {page === 'users' && sections.users && (
          <span className="ms-auto"><CreateTenantDialog onCreated={loadTenants} /></span>
        )}
      </div>
      <div className="mt-5 border-t border-border" />

      <div className="mt-5 flex flex-col gap-6" data-testid="tenant-admin-page">
        {page === 'users' && sections.users && (
          <nav className="flex gap-1.5" data-testid="admin-users-tabs">
            {/* SKU presets are instance-wide config → super-only (L2). Limited admins see users only. */}
            {[['users', 'tenantAdmin.usersTitle'], ['sku', 'tenantAdmin.presetsTitle']].filter(([k]) => isSuper || k !== 'sku').map(([k, lbl]) => (
              <button
                key={k}
                type="button"
                onClick={() => setUserTab(k)}
                data-testid={`admin-users-tab-${k}`}
                aria-current={userTab === k ? 'page' : undefined}
                className={`rounded-lg px-3 py-1.5 text-[13.5px]
                  ${userTab === k ? 'bg-accent font-bold text-accent-foreground' : 'text-foreground hover:bg-accent'}`}
              >
                {t(lbl)}
              </button>
            ))}
          </nav>
        )}
        {page === 'tenants' && sections.instance && (
          <InstanceSummary
            ctx={ctx}
            isSuper={isSuper}
            onPaymentsChanged={() => api.tenantAdmin('admin_context').then(setCtx).catch(() => {})}
          />
        )}

        {page === 'users' && userTab === 'users' && sections.users && (
          <SectionCard title={t('tenantAdmin.usersTitle')} testId="tenants-card">
            {tenants === null ? <Skeleton className="h-32 w-full" /> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="tenants-table">
                  <thead>
                    <tr className="text-start text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                      <th className="text-start py-2 pe-4"><T k="tenantAdmin.colEmail" /></th>
                      <th className="text-start py-2 pe-4"><T k="tenantAdmin.colRole" /></th>
                      <th className="text-start py-2 pe-4"><T k="tenantAdmin.colUserId" /></th>
                      {showWallet && <th className="text-start py-2 pe-4"><T k="tenantAdmin.colWallet" /></th>}
                      {showWallet && <th className="text-start py-2 pe-4"><T k="tenantAdmin.colTokens" /></th>}
                      <th className="text-start py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="font-data">
                    {tenants.map((tn) => (
                      <tr key={tn.id} className="border-b border-border" data-testid={`tenant-row-${tn.id}`}>
                        <td className="py-2 pe-4 text-foreground">{tn.email}</td>
                        <td className="py-2 pe-4">{tn.role || 'user'}</td>
                        <td className="py-2 pe-4"><CopyableUserId id={tn.id} /></td>
                        {showWallet && (
                          <td className="py-2 pe-4">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs border ${tn.wallet_enabled ? 'bg-warning-soft text-warning border-warning-border' : 'bg-muted text-muted-foreground border-border'}`}>
                              {tn.wallet_enabled ? t('tenantAdmin.walletOn') : t('tenantAdmin.walletOff')}
                            </span>
                          </td>
                        )}
                        {showWallet && <td className="py-2 pe-4">{(tn.total_tokens || 0).toLocaleString?.() ?? tn.total_tokens}</td>}
                        <td className="py-2 text-end">
                          <Button variant="outline" size="sm" onClick={() => loadDetail(tn.id)} data-testid={`tenant-manage-${tn.id}`}><T k="tenantAdmin.manage" /></Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        )}

        {page === 'users' && userTab === 'users' && sections.users && (
          <ManageTenant
            detail={detail}
            catalog={catalog}
            /* Metering toggle is a "make this user free" lever → super-only (L2). */
            showWallet={showWallet && isSuper}
            isSuper={isSuper}
            onClose={() => setDetail(null)}
            onSetRole={setRole}
            onToggle={toggleEnt}
            onWallet={setWallet}
            onSetLimited={setLimitedAdmin}
            onSaveCorpora={saveCorpora}
            onGranted={loadTenants}
          />
        )}

        {page === 'users' && userTab === 'sku' && sections.packages && isSuper && <PresetsManager presets={presets} catalog={catalog} onSaved={loadCatalog} />}

        {page === 'models' && sections.packages && <AiModelsProviders show="models" />}

        {/* Credit management as a DASHBOARD (Ali, 2026-08-05): the two money views open by
            default; every other box lives in its own tab. */}
        {page === 'credits' && sections.packages && (
          <nav className="flex gap-1.5 flex-wrap" data-testid="admin-credit-tabs">
            {[['dashboard', 'creditTabs.dashboard'], ['config', 'creditTabs.config'], ['bundles', 'creditTabs.bundles'], ['plans', 'creditTabs.plans'], ['promo', 'creditTabs.promo'], ['referral', 'creditTabs.referral'], ['tenants', 'creditTabs.tenants']]
              // Limited admins run credit management day-to-day but never the money SYSTEM:
              // config (peg/markup/switch), promo/gift codes (self-redeemable), referral config.
              .filter(([k]) => isSuper || !['config', 'promo', 'referral'].includes(k))
              .map(([k, lbl]) => (
              <button
                key={k}
                type="button"
                onClick={() => setCreditTab(k)}
                data-testid={`admin-credit-tab-${k}`}
                aria-current={creditTab === k ? 'page' : undefined}
                className={`rounded-lg px-3 py-1.5 text-[13.5px]
                  ${creditTab === k ? 'bg-accent font-bold text-accent-foreground' : 'text-foreground hover:bg-accent'}`}
              >
                {t(lbl)}
              </button>
            ))}
          </nav>
        )}
        {page === 'credits' && sections.packages && creditTab === 'dashboard' && <MoneyDashboard show="main" />}
        {page === 'credits' && sections.packages && creditTab === 'dashboard' && <IngestEconomicsCard isSuper={isSuper} />}
        {page === 'credits' && sections.packages && creditTab === 'dashboard' && <WalletDashboard show="overview" />}
        {page === 'credits' && sections.packages && creditTab === 'config' && isSuper && <WalletDashboard show="config" />}
        {page === 'credits' && sections.packages && creditTab === 'bundles' && <BundleAdmin />}
        {page === 'credits' && sections.packages && creditTab === 'plans' && <PlansAdmin />}
        {page === 'credits' && sections.packages && creditTab === 'plans' && <MoneyDashboard show="renewals" />}
        {page === 'credits' && sections.packages && creditTab === 'promo' && isSuper && <PromoAdmin />}
        {page === 'credits' && sections.packages && creditTab === 'referral' && isSuper && <ReferralAdmin />}
        {page === 'credits' && sections.packages && creditTab === 'tenants' && <MoneyDashboard show="tenants" />}

        {/* Redeem is NOT a credit-management section. The mock's tab list is prices, tenants,
            transactions, config, bundles, promo, free rounds, referral, plans - and redeeming a
            code is something a user does to their own wallet, which is why it lives in
            Settings -> Billing and still does. It was here because this page was built by
            grouping what the old one rendered rather than by reading the spec. */}

        {page === 'dimensions' && sections.dimensions && <DimensionsManager />}
      </div>
    </>
  );
}
